import {
  CreateRepeatDto,
  EnrollmentScheduleDto,
  RepeatDto,
  ScheduleDto,
} from '@malirang/api-client';
import { SchedulePeriodTypeMap } from '@malirang/common';
import { CalendarEvent } from '@malirang/front-share';
import dayjs from 'dayjs';

const daysOfWeekToKorean = [
  '일요일',
  '월요일',
  '화요일',
  '수요일',
  '목요일',
  '금요일',
  '토요일',
] as const;

const RepeatPeriodToKorean = {
  DAILY: '일',
  WEEKLY: '주',
  MONTHLY: '월',
  YEARLY: '년',
} as const;

export const ScheduleColor = {
  COUNSELING: 'counseling',
  SUPPLEMENT: 'supplement',
  CENTER: 'center',
  PERSONAL: 'personal',
  TREATMENT: 'treatment',
} as const;

/**
 * (일정) 요일을 한글로 변환
 * @param repeat
 * @returns
 */
const getDaysOfWeekToKorean = (repeat: RepeatDto) => {
  if (repeat) {
    return repeat.daysOfWeek
      ?.map((day: number) => daysOfWeekToKorean[day])
      .join(', ');
  }

  return '';
};

/**
 * (일정) 주기를 한글로 변환
 * @param repeat
 * @returns
 */
const getPeriodToKorean = (repeat: RepeatDto) => {
  if (repeat) {
    if (repeat.period === 1) {
      return `매${RepeatPeriodToKorean[repeat.periodType]}`;
    }

    return `${repeat.period}${RepeatPeriodToKorean[repeat.periodType]} 뒤`;
  }

  return '';
};

/**
 * (일정) 날짜를 한글로 변환
 * @param startAt
 * @param endAt
 * @param allDay
 * @returns
 */
const getFormatDate = (
  startAt: Date | string,
  endAt: Date | string,
  allDay?: boolean,
) => {
  const startDate = dayjs(startAt);
  const endDate = dayjs(endAt);

  if (allDay) {
    return `종일`;
  }

  if (startDate.isSame(endDate, 'day')) {
    const startTime = startDate.format('HH:mm');
    const endTime = endDate.format('HH:mm');

    if (startTime === endTime) {
      return `${startTime}`;
    }

    return `${startTime} ~ ${endTime}`;
  }

  return `${!allDay ? startDate.format('HH:mm') : ''} ~ ${!allDay ? endDate.format('HH:mm') : ''}`;
};

/**
 * 반복 일정일 때 일정이 몇 개 생성되는지 계산
 * @param startAt
 * @param repeat
 * @returns
 */
const calculateScheduleLength = (
  startAt: Date,
  repeat: CreateRepeatDto,
): number => {
  const { daysOfWeek, period, periodType, repeatEndAt } = repeat;

  if (!period) return 0;

  const unit = SchedulePeriodTypeMap[periodType]; // dayjs에서 사용할 단위 변환
  const endRepeatAt = dayjs(repeatEndAt);
  let currentPeriodStart = dayjs(startAt);
  let totalSchedules = 0;

  while (currentPeriodStart.isBefore(endRepeatAt)) {
    for (const dayOfWeek of daysOfWeek) {
      const currentDate = currentPeriodStart.day(dayOfWeek); // 현재 주기의 특정 요일

      // 종료일 이전인지 확인
      if (currentDate.isAfter(endRepeatAt)) continue;

      // 시작일 이후인지 확인
      if (currentDate.isBefore(dayjs(startAt))) continue;

      totalSchedules++;
    }

    // 다음 주기로 이동
    currentPeriodStart = currentPeriodStart.add(
      period,
      unit as dayjs.ManipulateType,
    );
  }

  return totalSchedules;
};

/**
 * schedule을 event로 변환
 * @param schedule
 * @returns
 */
const scheduleToEvent = (
  schedule: ScheduleDto,
  color?: string,
): CalendarEvent => {
  return {
    title: schedule.title,
    startDate: dayjs(schedule.startAt).toDate(),
    endDate: dayjs(schedule.endAt).toDate(),
    color: color || '#1693C9',
    id: schedule.id,
    allDay: schedule.allDay,
    repeat: !!schedule.repeat,
    metadata: {
      type: schedule.type,
      memo: schedule.memo,
      repeat: schedule.repeat,
    },
  };
};

/**
 * enrollmentSchedule을 event로 변환
 * @param schedule
 * @returns
 */
const enrollmentScheduleToEvent = (
  schedule: EnrollmentScheduleDto,
): CalendarEvent => {
  return {
    title: schedule.title,
    startDate: dayjs(schedule.startAt).toDate(),
    endDate: dayjs(schedule.endAt).toDate(),
    color: schedule.color,
    id: schedule.id,
    allDay: schedule.allDay,
    repeat: !!schedule.repeat,
    metadata: {
      selfPayAmount: schedule.selfPayAmount,
      supportAmount: schedule.supportAmount,
      ticketAmount: schedule.ticketAmount,
      voucher: schedule.voucher,
      ticketName: schedule.ticketName,
      ticketTypes: schedule.ticketTypes,
      type: schedule.type,
      memo: schedule.memo,
      ticket: schedule.ticket,
      repeat: schedule.repeat,
      enrollment: schedule.enrollment,
      supplementAt: schedule.supplementSchedule?.startAt
        ? dayjs(schedule.supplementSchedule?.startAt).toDate()
        : undefined,
    },
  };
};

export const ScheduleHelper = {
  getDaysOfWeekToKorean,
  getPeriodToKorean,
  getFormatDate,
  calculateScheduleLength,
  scheduleToEvent,
  enrollmentScheduleToEvent,
};
